import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { AttemptsState, UseAttemptsReturn } from "./type";
import * as topikAPI from "../../Data/Topik";

// Attempts state atom
export const attemptsState = atom<AttemptsState>({
    key: "attemptsState" + randomString(4),
    default: {
        items: {},
        answers: {},
        currentAttemptId: null,
        loading: false,
        error: null
    }
});

export const useAttempts = (): UseAttemptsReturn => {
    const [state, setState] = useRecoilState(attemptsState);
    const resetAttempts = useResetRecoilState(attemptsState);

    // List user's attempts
    const syncAttempts = useCallback(async (params?: { 
        userid?: string;
        examid?: string;
        status?: string;
        limit?: number;
        offset?: number;
    }): Promise<topikAPI.AttemptType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const attempts = await topikAPI.listAttempts(params);
            
            if (attempts) {
                const items = Object.fromEntries(
                    attempts.map(a => [a.attemptid, a])
                );
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    loading: false
                }));
                
                return attempts;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return [];
        } catch (error) {
            console.error('Failed to list attempts:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to load attempts' 
            }));
            return [];
        }
    }, [setState]);

    // Get specific attempt with answers
    const getAttempt = useCallback(async (attemptid: string): Promise<topikAPI.AttemptWithAnswers | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const attempt = await topikAPI.getAttempt(attemptid);
            
            if (attempt) {
                // Update the attempt in state (without answers array)
                const { answers, ...attemptData } = attempt;
                
                // Convert answers array to map
                const answersMap: Record<number, string> = {};
                if (answers) {
                    answers.forEach(answer => {
                        answersMap[answer.questionid] = answer.answer;
                    });
                }
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [attemptid]: attemptData },
                    answers: { ...prev.answers, [attemptid]: answersMap },
                    loading: false
                }));
                
                return attempt;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to get attempt:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to load attempt' 
            }));
            return null;
        }
    }, [setState]);

    // Create new attempt
    const createAttempt = useCallback(async (params: { 
        examid?: string;
        questionnaireid?: string;
        userid?: string;
        section?: 'listening' | 'reading' | 'writing';
    }): Promise<topikAPI.CreateAttemptResponse | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const response = await topikAPI.createAttempt(params);
            
            if (response) {
                const attempt: topikAPI.AttemptType = {
                    attemptid: response.attemptid,
                    examid: response.examid,
                    questionnaireid: response.questionnaireid,
                    userid: response.userid,
                    section: response.section,
                    status: response.status as 'pending' | 'active' | 'completed' | 'expired',
                    started_at: null,
                    finished_at: null,
                    listening_score: null,
                    reading_score: null,
                    writing_score: null,
                    total_score: null,
                    created_at: response.created_at,
                    updated_at: response.updated_at
                };
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [response.attemptid]: attempt },
                    currentAttemptId: response.attemptid,
                    loading: false
                }));
                
                return response;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to create attempt:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to create attempt' 
            }));
            return null;
        }
    }, [setState]);

    // Start attempt
    const startAttempt = useCallback(async (attemptid: string): Promise<topikAPI.UpdateAttemptResponse | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const response = await topikAPI.startAttempt(attemptid);
            
            if (response) {
                setState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [attemptid]: {
                            ...prev.items[attemptid],
                            status: response.status as 'pending' | 'active' | 'completed' | 'expired',
                            started_at: response.started_at || null,
                            updated_at: response.updated_at
                        }
                    },
                    loading: false
                }));
                
                return response;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to start attempt:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to start attempt' 
            }));
            return null;
        }
    }, [setState]);

    // Submit attempt
    const submitAttempt = useCallback(async (
        attemptid: string, 
        answers?: topikAPI.AnswerSubmission[]
    ): Promise<topikAPI.UpdateAttemptResponse | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const response = await topikAPI.submitAttempt(attemptid, answers);
            
            if (response) {
                setState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [attemptid]: {
                            ...prev.items[attemptid],
                            status: response.status as 'pending' | 'active' | 'completed' | 'expired',
                            finished_at: response.finished_at || null,
                            listening_score: response.listening_score || null,
                            reading_score: response.reading_score || null,
                            writing_score: response.writing_score || null,
                            total_score: response.total_score || null,
                            updated_at: response.updated_at
                        }
                    },
                    loading: false
                }));
                
                return response;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to submit attempt:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to submit attempt' 
            }));
            return null;
        }
    }, [setState]);

    // Submit individual answer
    const submitAnswer = useCallback(async ({
        attemptid, questionid, answer
    }:{
        attemptid: string,
        questionid: number,
        answer: string
    }): Promise<topikAPI.SubmitAnswerResponse | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const response = await topikAPI.submitAnswer({
                attemptid,
                questionid,
                answer
            });
            
            if (response) {
                // Update local answers state
                setState(prev => ({
                    ...prev,
                    answers: {
                        ...prev.answers,
                        [attemptid]: {
                            ...prev.answers[attemptid],
                            [questionid]: answer
                        }
                    },
                    loading: false
                }));
                return response;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to submit answer:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to submit answer' 
            }));
            return null;
        }
    }, [setState]);

    // Get answers for a specific attempt
    const getAnswersForAttempt = useCallback((attemptid: string): Record<number, string> => {
        return state.answers[attemptid] || {};
    }, [state.answers]);

    // Set current attempt
    const setCurrentAttempt = useCallback((attemptid: string | null) => {
        setState(prev => ({ ...prev, currentAttemptId: attemptid }));
    }, [setState]);

    const reset = useCallback(() => {
        resetAttempts();
    }, [resetAttempts]);

    return {
        attempts: state.items,
        answers: state.answers,
        currentAttemptId: state.currentAttemptId,
        loading: state.loading,
        error: state.error,
        syncAttempts,
        getAttempt,
        createAttempt,
        startAttempt,
        submitAttempt,
        submitAnswer,
        getAnswersForAttempt,
        setCurrentAttempt,
        reset
    };
};